import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import CanonicalTag from '@/components/CanonicalTag';
import MetaTags from '@/components/MetaTags';
import { useParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Calendar, User, Clock, Tag, BookOpen, Share2, Heart, MessageCircle, Eye, Send } from 'lucide-react';
import { wordpressService, WordPressArticle, WordPressComment } from '@/services/wordpress';
import { ARTICLE_IMAGE_FALLBACK, normalizeArticleImageUrl } from '@/lib/articleImage';

type PrerenderStore = {
    articleBySlug?: Record<string, WordPressArticle>;
};

const getPrerenderedArticle = (slug?: string): WordPressArticle | null => {
    if (!slug) {
        return null;
    }
    const store = (globalThis as { __SALAMAT_PRERENDER__?: PrerenderStore }).__SALAMAT_PRERENDER__;
    return store?.articleBySlug?.[slug] ?? null;
};

type CommentNode = WordPressComment & {
    children: WordPressComment[];
};

const SingleArticle = () => {
    const { slug } = useParams<{
        slug?: string;
    }>();
    const initialArticle = getPrerenderedArticle(slug);
    const [article, setArticle] = useState<WordPressArticle | null>(initialArticle);
    const [loading, setLoading] = useState(!initialArticle);
    const [error, setError] = useState<string | null>(null);
    const [comments, setComments] = useState<WordPressComment[]>([]);
    const [related, setRelated] = useState<WordPressArticle[]>([]);
    const [isLiked, setIsLiked] = useState(false);
    const [viewCount, setViewCount] = useState(0);
    
    // Comment form state
    const [commentName, setCommentName] = useState('');
    const [commentEmail, setCommentEmail] = useState('');
    const [commentContent, setCommentContent] = useState('');
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentSuccess, setCommentSuccess] = useState(false);
    const [commentError, setCommentError] = useState('');
    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const target = event.currentTarget;
        if (target.dataset.fallbackApplied === '1') {
            return;
        }
        target.src = ARTICLE_IMAGE_FALLBACK;
        target.dataset.fallbackApplied = '1';
    };
    
    const commentTree: CommentNode[] = React.useMemo(() => {
        const normalized = comments
            .slice()
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const byId = new Map<number, CommentNode>();
        normalized.forEach((comment) => {
            byId.set(comment.id, {
                ...comment,
                children: []
            });
        });

        const roots: CommentNode[] = [];
        normalized.forEach((comment) => {
            const node = byId.get(comment.id);
            if (!node) return;
            const parentId = comment.parent ?? 0;
            if (parentId && byId.has(parentId)) {
                byId.get(parentId)?.children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    }, [comments]);
    useEffect(() => {
        let isMounted = true;
        const fetchArticle = async () => {
            if (!slug)
                return;

            const hasInitialArticle = Boolean(initialArticle && slug && initialArticle.slug === slug);

            try {
                if (!hasInitialArticle) {
                    setLoading(true);
                }
                setError(null);
                const post = await wordpressService.fetchPostBySlug(slug);
                const convertedArticle = wordpressService.convertToArticle(post);
                if (!isMounted)
                    return;
                setArticle(convertedArticle);
                setViewCount(Math.floor(Math.random() * 1000) + 100);
                setLoading(false);
                const [postComments, relatedArticles] = await Promise.all([
                    wordpressService.fetchComments(post.id),
                    wordpressService.getRelatedArticles(post, 3)
                ]);
                if (!isMounted)
                    return;
                setComments(postComments || []);
                setRelated(relatedArticles || []);
            }
            catch (err) {
                if (!isMounted)
                    return;
                if (!hasInitialArticle) {
                    setError('خطا در بارگذاری مقاله');
                }
                console.error('Error fetching article:', err);
                if (!hasInitialArticle) {
                    setLoading(false);
                }
            }
        };
        fetchArticle();
        return () => {
            isMounted = false;
        };
    }, [slug, initialArticle]);
    const handleLike = () => {
        setIsLiked(!isLiked);
    };
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: article?.title,
                text: article?.excerpt,
                url: window.location.href,
            });
        }
        else {
            navigator.clipboard.writeText(window.location.href);
        }
    };

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!commentName.trim() || !commentEmail.trim() || !commentContent.trim()) {
            setCommentError('لطفاً تمام فیلدها را پر کنید');
            return;
        }
        
        if (!article?.id) {
            setCommentError('خطا در ارسال کامنت');
            return;
        }
        
        setCommentSubmitting(true);
        setCommentError('');
        setCommentSuccess(false);
        
        try {
            await wordpressService.submitComment({
                post: article.id,
                author_name: commentName,
                author_email: commentEmail,
                content: commentContent
            });
            
            setCommentSuccess(true);
            setCommentName('');
            setCommentEmail('');
            setCommentContent('');
            
            // Refresh comments after 1 second
            setTimeout(async () => {
                try {
                    const updatedComments = await wordpressService.fetchComments(article.id);
                    setComments(updatedComments || []);
                    setCommentSuccess(false);
                } catch (err) {
                    console.error('Error refreshing comments:', err);
                }
            }, 1000);
            
        } catch (err: any) {
            console.error('Error submitting comment:', err);
            setCommentError(err.message || 'خطا در ارسال کامنت. لطفاً دوباره تلاش کنید.');
        } finally {
            setCommentSubmitting(false);
        }
    };

    if (loading) {
        return (<div className="min-h-screen bg-white flex items-center justify-center">
        <Header />
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 animate-pulse">
            <BookOpen className="w-8 h-8 text-blue-600"/>
          </div>
          <p className="text-gray-600">در حال بارگذاری مقاله...</p>
        </div>
      </div>);
    }
    if (error || !article) {
        return (<div className="min-h-screen bg-white flex items-center justify-center">
        <Header />
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <h1 className="text-xl font-bold text-red-600 mb-2">خطا</h1>
          <p className="text-gray-600 mb-4">{error || 'مقاله پیدا نشد'}</p>
          <Link to="/articles">
            <Button variant="outline">
              بازگشت به مقالات
            </Button>
          </Link>
        </div>
      </div>);
    }
    return (<div className="min-h-screen bg-white">
      <CanonicalTag path={`/articles/${article.slug}`}/>
      <MetaTags title={`${article.title} | آزمایشگاه تشخیص پزشکی سلامت`} description={article.excerpt || `مقاله ${article.title} در آزمایشگاه تشخیص پزشکی سلامت`} keywords={`${article.category}, آزمایشگاه سلامت, ${article.tags?.join(', ') || ''}`} ogImage={article.image} path={`/articles/${article.slug}`}/>
      <Header />
      
      
      <div className="container mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-8 sm:pb-12">
        <div className="max-w-4xl mx-auto">
          
          <div className="mb-8">
            <Link to="/articles">
              <Button variant="ghost" className="text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowRight className="ml-2 h-4 w-4"/>
                بازگشت به مقالات
              </Button>
            </Link>
          </div>
          
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6">
            {article.title}
          </h1>

          
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600 mb-6 sm:mb-8">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4"/>
              <span>{article.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4"/>
              <span>{article.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4"/>
              <span>{article.readTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4"/>
              <span>{viewCount} بازدید</span>
            </div>
          </div>
        </div>
      </div>

      
      <div className="container mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="max-w-4xl mx-auto">
          
            <div className="mb-8 sm:mb-12 overflow-hidden rounded-2xl shadow-md">
            <img src={normalizeArticleImageUrl(article.image)} alt={article.title} className="w-full h-56 sm:h-64 md:h-96 object-cover" onError={handleImageError}/>
          </div>
          
          
          <div className="bg-white">
            
            {article.excerpt && (<div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gray-50 rounded-2xl border-r-4 border-blue-500">
                <p className="text-gray-700 leading-loose text-base sm:text-lg font-medium">
                  {article.excerpt}
                </p>
              </div>)}

            
            <div className="article-prose prose prose-base sm:prose-lg max-w-none text-gray-800 prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-loose prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-blockquote:border-r-4 prose-blockquote:border-blue-500 prose-blockquote:pr-6 prose-blockquote:pl-0 prose-blockquote:italic prose-blockquote:text-gray-600 prose-img:rounded-2xl prose-img:shadow-md prose-img:my-6" dangerouslySetInnerHTML={{ __html: article.content }}/>

            
            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-4 sm:gap-6">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4"/>
                    <span>نویسنده: {article.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4"/>
                    <span>تاریخ انتشار: {article.date}</span>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={handleLike} variant={isLiked ? "default" : "outline"} size="sm" className={`${isLiked ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}>
                    <Heart className={`w-4 h-4 ml-2 ${isLiked ? 'fill-current' : ''}`}/>
                    {isLiked ? 'پسندیده شد' : 'پسندیدن'}
                  </Button>
                  <Button onClick={handleShare} variant="outline" size="sm">
                    <Share2 className="w-4 h-4 ml-2"/>
                    اشتراک‌گذاری
                  </Button>
                </div>
              </div>
            </div>

            
            {article.tags && article.tags.length > 0 && (<div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gray-50 rounded-2xl">
                <div className="mb-4 text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-blue-600"/>
                  برچسب‌ها
                </div>
                <div className="flex flex-wrap gap-3">
                  {article.tags.map((tag) => (<Badge key={tag} variant="secondary" className="px-3 py-1 text-sm bg-white border border-gray-200 hover:border-blue-300 transition-colors">
                      #{tag}
                    </Badge>))}
                </div>
              </div>)}

            
            <div className="mt-12">
              <div className="border-t border-gray-200 pt-8">
                <div className="mb-6 flex items-center gap-3">
                  <MessageCircle className="w-6 h-6 text-blue-600"/>
                  <h3 className="text-2xl font-bold text-gray-800">نظرات</h3>
                  <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                    {comments.length} نظر
                  </Badge>
                </div>
                
                {/* Comment Form */}
                <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <h5 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-600"/>
                    دیدگاه خود را بنویسید
                  </h5>
                  
                  {commentSuccess && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                      ✓ نظر شما با موفقیت ثبت شد و پس از تأیید نمایش داده خواهد شد.
                    </div>
                  )}
                  
                  {commentError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      {commentError}
                    </div>
                  )}
                  
                  <form onSubmit={handleCommentSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="comment-name" className="block text-sm font-medium text-gray-700 mb-2">
                          نام و نام خانوادگی *
                        </label>
                        <input
                          type="text"
                          id="comment-name"
                          value={commentName}
                          onChange={(e) => setCommentName(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="نام خود را وارد کنید"
                          required
                          disabled={commentSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="comment-email" className="block text-sm font-medium text-gray-700 mb-2">
                          ایمیل *
                        </label>
                        <input
                          type="email"
                          id="comment-email"
                          value={commentEmail}
                          onChange={(e) => setCommentEmail(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="example@email.com"
                          required
                          disabled={commentSubmitting}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="comment-content" className="block text-sm font-medium text-gray-700 mb-2">
                        متن دیدگاه *
                      </label>
                      <textarea
                        id="comment-content"
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        placeholder="نظر خود را بنویسید..."
                        required
                        disabled={commentSubmitting}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        نظرات پس از تأیید مدیر سایت نمایش داده می‌شوند
                      </p>
                      <Button 
                        type="submit" 
                        disabled={commentSubmitting}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2.5 rounded-xl flex items-center gap-2 transition-all"
                      >
                        {commentSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            در حال ارسال...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            ارسال نظر
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>

                {/* Existing Comments */}
                {commentTree.length === 0 ? (<div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-gray-400"/>
                    </div>
                    <p className="text-gray-500">اولین نفری باشید که نظر می‌دهید</p>
                  </div>) : (<div className="space-y-6">
                    {commentTree.map((comment) => (<div key={comment.id} className="space-y-4">
                        <div className="p-4 sm:p-6 border border-gray-100 rounded-2xl">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {comment.author_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{comment.author_name}</div>
                              <div className="text-xs text-gray-500">{new Date(comment.date).toLocaleDateString('fa-IR')}</div>
                            </div>
                          </div>
                          <div className="article-prose prose prose-sm max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: comment.content.rendered }}/>
                        </div>

                        {comment.children.length > 0 && (
                          <div className="mr-4 sm:mr-8 space-y-3 border-r-2 border-blue-100 pr-3 sm:pr-4">
                            {comment.children.map((reply) => (
                              <div key={reply.id} className="p-3 sm:p-4 border border-blue-100 bg-blue-50/40 rounded-xl">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                    {reply.author_name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium text-gray-800 text-sm">{reply.author_name}</div>
                                    <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-700 border-0">
                                      پاسخ
                                    </Badge>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 mb-2">{new Date(reply.date).toLocaleDateString('fa-IR')}</div>
                                <div className="article-prose prose prose-sm max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: reply.content.rendered }}/>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>))}
                  </div>)}
              </div>
            </div>

            
            {related.length > 0 && (<div className="mt-8 sm:mt-12 border-t border-gray-200 pt-6 sm:pt-8">
                <h4 className="font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2 text-base sm:text-lg">
                  <BookOpen className="w-5 h-5 text-blue-600"/>
                  مقالات مرتبط
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {related.map((rel) => (<Link to={`/articles/${rel.slug}`} key={rel.id} className="block">
                      <div className="group border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
                    <img src={normalizeArticleImageUrl(rel.image)} alt={rel.title} className="w-full h-32 sm:h-36 object-cover group-hover:scale-105 transition-transform duration-300 rounded-t-2xl" onError={handleImageError}/>
                        <div className="p-3 sm:p-4">
                          <div className="text-sm text-gray-500 mb-2">{rel.date}</div>
                          <h5 className="font-semibold text-gray-800 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300">{rel.title}</h5>
                        </div>
                      </div>
                    </Link>))}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>);
};
export default SingleArticle;
